/**
 * All 6 dimension scoring functions used by the inference engine.
 * Phase 2: budget, location, deadline now have real implementations.
 */

// ─── TASK 23 (Phase 1) ───────────────────────────────────────────────────────

/**
 * Gaussian curve centered on the midpoint of [jobMin, jobMax].
 * Returns 0-100. Null inputs return 50 (neutral).
 */
export function experienceScore(
  candidateYears: number | null | undefined,
  jobMin: number | null | undefined,
  jobMax: number | null | undefined
): number {
  if (candidateYears == null || (jobMin == null && jobMax == null)) return 50;

  const min = jobMin ?? 0;
  const max = jobMax ?? min;
  const midpoint = (min + max) / 2;
  const spread = Math.max(max - min, 2); // avoid zero spread

  const diff = candidateYears - midpoint;
  // Gaussian: score = 100 * exp(-0.5 * (diff / (spread/2))^2)
  const sigma = spread / 2;
  const score = 100 * Math.exp(-0.5 * Math.pow(diff / sigma, 2));

  return Math.round(Math.max(0, Math.min(100, score)));
}

// ─── TASK 34: Budget fit ──────────────────────────────────────────────────────

/**
 * Returns 0-100.
 * - Candidate expected CTC within job budget range → 100
 * - Within 20% outside the range → 70 (proportionally reduced)
 * - Beyond 20% outside → proportional decrease to 0
 * - Either null → 50 (neutral)
 */
export function budgetScore(
  candidateExpected: number | null | undefined,
  jobMin: number | null | undefined,
  jobMax: number | null | undefined
): number {
  if (candidateExpected == null || (jobMin == null && jobMax == null)) return 50;

  const min = jobMin ?? 0;
  const max = jobMax ?? min;

  // Candidate is within the budget range
  if (candidateExpected >= min && candidateExpected <= max) return 100;

  // Effective range width (use at least 1 to avoid division by zero)
  const rangeWidth = Math.max(max - min, 1);
  const tolerance = rangeWidth * 0.20; // 20% tolerance band

  if (candidateExpected < min) {
    // Candidate cheaper than minimum — acceptable, slightly below ideal
    const gap = min - candidateExpected;
    if (gap <= tolerance) return Math.round(70 - (gap / tolerance) * 30); // 70→40
    return Math.max(0, Math.round(40 - ((gap - tolerance) / (min * 0.5 + 1)) * 40));
  } else {
    // Candidate more expensive than maximum
    const gap = candidateExpected - max;
    if (gap <= tolerance) return Math.round(70 - (gap / tolerance) * 40); // 70→30
    return Math.max(0, Math.round(30 - ((gap - tolerance) / (max * 0.3 + 1)) * 30));
  }
}

// ─── TASK 35: Location fit ────────────────────────────────────────────────────

/**
 * Returns 0-100.
 * - Remote job → 100 for everyone
 * - Candidate preferred_locations includes job location → 100
 * - Candidate location matches job location (case-insensitive substring) → 100
 * - Else → 30
 */
export function locationScore(
  candidateLocation: string | null | undefined,
  candidatePreferred: string[] | null | undefined,
  jobLocation: string | null | undefined,
  remotePolicy: string | null | undefined
): number {
  // Remote job: anyone can work there
  if (remotePolicy === 'remote') return 100;

  if (jobLocation == null) return 50; // no location requirement → neutral

  const jobLoc = jobLocation.toLowerCase().trim();

  // Check preferred locations list
  if (Array.isArray(candidatePreferred) && candidatePreferred.length > 0) {
    const match = candidatePreferred.some(
      (pl) => pl.toLowerCase().trim() === jobLoc || pl.toLowerCase().includes(jobLoc) || jobLoc.includes(pl.toLowerCase().trim())
    );
    if (match) return 100;
  }

  // Check candidate current location
  if (candidateLocation) {
    const cLoc = candidateLocation.toLowerCase().trim();
    if (cLoc === jobLoc || cLoc.includes(jobLoc) || jobLoc.includes(cLoc)) return 100;
  }

  // Hybrid: partial credit — candidate may be willing to commute
  if (remotePolicy === 'hybrid') return 60;

  return 30;
}

// ─── TASK 36: Deadline feasibility ────────────────────────────────────────────

/**
 * Returns 0-100.
 * - notice_period_days <= days_remaining → 100
 * - Within 7 days of deadline → 60
 * - notice_period > deadline → proportional decrease to 0
 * - Either null → 50 (neutral)
 */
export function deadlineScore(
  noticePeriodDays: number | null | undefined,
  hiringDeadline: string | null | undefined
): number {
  if (noticePeriodDays == null || hiringDeadline == null) return 50;

  const now = Date.now();
  const deadline = new Date(hiringDeadline).getTime();
  const daysRemaining = Math.round((deadline - now) / 86_400_000);

  if (daysRemaining <= 0) return 50; // deadline already passed → neutral (don't punish)

  const gap = noticePeriodDays - daysRemaining; // positive = candidate can't make it

  if (gap <= 0) return 100; // can easily start before deadline

  if (gap <= 7) return 60; // within a week — negotiable

  // Proportional decrease: every 10 days beyond deadline = -10 points from 50
  const penalty = Math.min(50, Math.round((gap / 10) * 10));
  return Math.max(0, 50 - penalty);
}
